/**
 * Rekenen met kloktijd in Europe/Amsterdam (werktijden, werkdagen), zonder extra bibliotheek.
 * Werkt met de tijdzonegegevens van Intl, dus ook rond de wissel van zomer- en wintertijd.
 */
export const TIME_ZONE = "Europe/Amsterdam";

export interface LocalParts {
  year: number;
  month: number; // 1–12
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** 0 = zondag … 6 = zaterdag */
  weekday: number;
}

const fmt = new Intl.DateTimeFormat("en-GB", {
  timeZone: TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

export function amsterdamParts(date: Date): LocalParts {
  const p = Object.fromEntries(fmt.formatToParts(date).map((x) => [x.type, x.value]));
  const year = Number(p.year);
  const month = Number(p.month);
  const day = Number(p.day);
  return {
    year,
    month,
    day,
    hour: Number(p.hour),
    minute: Number(p.minute),
    second: Number(p.second),
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  };
}

/** Kloktijd in Amsterdam → moment (UTC). Bij een niet-bestaande tijd (zomertijdsprong) een uur later. */
export function fromAmsterdam(year: number, month: number, day: number, hour = 0, minute = 0, second = 0): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute, second);
  // Twee rondes: verschil tussen de gewenste en de getoonde kloktijd corrigeren.
  let t = guess;
  for (let i = 0; i < 2; i++) {
    const p = amsterdamParts(new Date(t));
    const shown = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    t += guess - shown;
  }
  return new Date(t);
}

/** Dezelfde kloktijd een aantal kalenderdagen later (of eerder). */
export function addLocalDays(date: Date, days: number): Date {
  const p = amsterdamParts(date);
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day + days));
  return fromAmsterdam(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), p.hour, p.minute, p.second);
}

export const isWeekend = (date: Date) => {
  const w = amsterdamParts(date).weekday;
  return w === 0 || w === 6;
};

/** Dezelfde kloktijd een aantal werkdagen (ma–vr) later. Feestdagen tellen (nog) als werkdag. */
export function addWorkdays(date: Date, n: number): Date {
  let d = date;
  let left = n;
  while (left > 0) {
    d = addLocalDays(d, 1);
    if (!isWeekend(d)) left--;
  }
  return d;
}

/** Datum (YYYY-MM-DD) in Amsterdam. */
export function amsterdamDate(date: Date): string {
  const p = amsterdamParts(date);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

export const WORK_START_HOUR = 8;
export const WORK_END_HOUR = 18;

/** Werkdag, 8:00 tot 18:00 in Amsterdam. */
export function isWorkHours(date: Date): boolean {
  if (isWeekend(date)) return false;
  const h = amsterdamParts(date).hour;
  return h >= WORK_START_HOUR && h < WORK_END_HOUR;
}

/** Het eerste moment binnen werktijden vanaf `date` (date zelf als dat al binnen werktijden valt). */
export function nextWorkHoursStart(date: Date): Date {
  if (isWorkHours(date)) return date;
  const p = amsterdamParts(date);
  let day = fromAmsterdam(p.year, p.month, p.day, WORK_START_HOUR);
  if (p.hour >= WORK_END_HOUR || day.getTime() <= date.getTime()) day = addLocalDays(day, 1);
  while (isWeekend(day)) day = addLocalDays(day, 1);
  return day;
}

/** Een aantal werkuren (ma–vr, 8:00–18:00) na `date`; voor de reactietijd van support. */
export function addWorkHours(date: Date, hours: number): Date {
  let t = nextWorkHoursStart(date);
  let left = hours * 3_600_000;
  for (;;) {
    const p = amsterdamParts(t);
    const end = fromAmsterdam(p.year, p.month, p.day, WORK_END_HOUR);
    const room = end.getTime() - t.getTime();
    if (left <= room) return new Date(t.getTime() + left);
    left -= room;
    t = nextWorkHoursStart(end);
  }
}
